import { useState } from 'react';

import { NgButton } from '@/ui';

import { DevJsonDetails } from './DevJsonDetails';
import {
  HUB_CONNECTION_EVENT_NAMES,
  PORTAL_HUB_EVENT_NAMES,
  filterRecordedEvents,
  formatEventTime,
} from './portalHubEventLog';
import { EVENT_LOG_CAPACITIES } from './useDevPortalHubLog';
import type { DevPortalHubLog } from './useDevPortalHubLog';

export type DevHubEventLogPanelProps = {
  log: DevPortalHubLog;
};

const ALL_EVENT_NAMES = [...PORTAL_HUB_EVENT_NAMES, ...HUB_CONNECTION_EVENT_NAMES];

export function DevHubEventLogPanel({ log }: DevHubEventLogPanelProps) {
  const [name, setName] = useState('');
  const [text, setText] = useState('');

  const shown = filterRecordedEvents(log.events, { name, text });

  return (
    <section>
      <h2 className="h5">Incoming hub events</h2>
      <div className="small text-body-secondary mb-2">
        Recorded only while this page is open. Outgoing invocations are not observable from here.
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="dev-hub-log-recording"
            checked={log.isRecording}
            onChange={(event) => log.setRecording(event.target.checked)}
          />
          <label className="form-check-label small" htmlFor="dev-hub-log-recording">
            Recording
          </label>
        </div>

        <select
          className="form-select form-select-sm w-auto"
          aria-label="Event name filter"
          value={name}
          onChange={(event) => setName(event.target.value)}
        >
          <option value="">All events</option>
          {ALL_EVENT_NAMES.map((eventName) => (
            <option key={eventName} value={eventName}>
              {eventName} ({log.countsByName.get(eventName) ?? 0})
            </option>
          ))}
        </select>

        <input
          className="form-control form-control-sm w-auto"
          type="search"
          placeholder="Filter payload"
          aria-label="Payload filter"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />

        <select
          className="form-select form-select-sm w-auto"
          aria-label="Kept events"
          value={log.capacity}
          onChange={(event) => log.setCapacity(Number(event.target.value))}
        >
          {EVENT_LOG_CAPACITIES.map((capacity) => (
            <option key={capacity} value={capacity}>
              keep {capacity}
            </option>
          ))}
        </select>

        <NgButton cssClass="btn-sm" onClick={log.clear}>
          Clear
        </NgButton>

        <span className="small text-body-secondary">
          {log.received} received, {log.events.length} kept, {shown.length} shown
        </span>
      </div>

      <details className="mb-2">
        <summary className="small text-body-secondary" style={{ cursor: 'pointer' }}>
          Counters
        </summary>
        <div className="d-flex flex-wrap gap-1 mt-1">
          {ALL_EVENT_NAMES.map((eventName) => {
            const count = log.countsByName.get(eventName) ?? 0;
            return (
              <span
                key={eventName}
                className={`badge ${count === 0 ? 'text-bg-light text-body-secondary' : 'text-bg-primary'}`}
              >
                {eventName} {count}
              </span>
            );
          })}
        </div>
      </details>

      {shown.length === 0 ? (
        <div className="small text-body-secondary">
          {log.events.length === 0 ? 'Nothing has arrived since the page opened.' : 'No event matches the filter.'}
        </div>
      ) : (
        <div className="table-responsive" style={{ maxHeight: '32rem', overflow: 'auto' }}>
          <table className="table table-sm table-hover small font-monospace align-middle">
            <thead className="sticky-top bg-body">
              <tr>
                <th>#</th>
                <th>Time</th>
                <th>Event</th>
                <th>Incident</th>
                <th>Payload</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((event) => (
                <tr key={event.sequence} className={event.source === 'connection' ? 'table-warning' : undefined}>
                  <td>{event.sequence}</td>
                  <td>{formatEventTime(event.receivedAt)}</td>
                  <td>{event.name}</td>
                  <td title={event.incidentId ?? ''}>{event.incidentId?.slice(0, 8) ?? '—'}</td>
                  <td className="text-break" style={{ maxWidth: '50rem' }}>
                    {event.args.length === 0 ? (
                      <span className="text-body-secondary">no payload</span>
                    ) : (
                      <DevJsonDetails summary={event.summary === '' ? 'json' : event.summary} value={event.args} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
